import { useEffect, useState } from 'react';
import { ExternalLink, Maximize2, RotateCcw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getSiteContentById } from '../lib/siteContent';
import { navigateToSitePage, shouldShowPageFrame } from '../lib/avatarActions';
import { embedFrameSrc } from '../lib/avatarEmbed';
import { resetAvatarSplitContent, subscribeAvatarSplit } from '../lib/avatarSplit';

function resolveFrameUrl(route: string): string | null {
  if (route.startsWith('http') || route.startsWith('/')) return embedFrameSrc(route);
  return null;
}

/** Always-visible right panel on the avatar homepage */
export default function AvatarStaticSplitPanel() {
  const navigate = useNavigate();
  const [pageId, setPageId] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const unsubscribe = subscribeAvatarSplit(setPageId);
    const onOpenSplit = (e: Event) => {
      const id = (e as CustomEvent<{ id: string }>).detail?.id;
      if (id) setPageId(id);
    };
    const onClear = () => setPageId(null);
    const onReload = () => setReloadKey((k) => k + 1);
    window.addEventListener('selflabs:open-split', onOpenSplit);
    window.addEventListener('selflabs:open-split-clear', onClear);
    window.addEventListener('selflabs:open-split-reload', onReload);
    return () => {
      unsubscribe();
      window.removeEventListener('selflabs:open-split', onOpenSplit);
      window.removeEventListener('selflabs:open-split-clear', onClear);
      window.removeEventListener('selflabs:open-split-reload', onReload);
    };
  }, []);

  const item = pageId ? getSiteContentById(pageId) : undefined;
  const frameUrl = item && shouldShowPageFrame(item) ? resolveFrameUrl(item.ctaHref) : null;
  const showContent = Boolean(pageId && frameUrl);

  const openFullPage = () => {
    if (!item) return;
    navigateToSitePage(item.id, navigate, { forceFullNavigation: true });
  };

  return (
    <aside
      className="avatar-split-panel-static flex flex-col h-full overflow-hidden bg-black border-t md:border-t-0 md:border-l border-white/10"
      aria-label="Site content panel"
    >
      <header className="flex items-center gap-3 px-4 py-3 border-b border-white/10 bg-black/95 shrink-0">
        <div className="flex-1 min-w-0">
          <p className="text-xs text-white/45 uppercase tracking-wider">Explore SELF Labs</p>
          <h2 className="text-base font-semibold text-white truncate">
            {showContent ? (item?.title ?? 'SELF Labs') : 'Content preview'}
          </h2>
        </div>
        {showContent && item && frameUrl && (
          <>
            <button
              type="button"
              onClick={openFullPage}
              className="p-2 rounded-lg hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
              title="Open full page"
              aria-label="Open full page"
            >
              <Maximize2 className="w-4 h-4" />
            </button>
            {item.ctaHref.startsWith('http') && (
              <a
                href={item.ctaHref}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-lg hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
                aria-label="Open in new tab"
              >
                <ExternalLink className="w-4 h-4" />
              </a>
            )}
          </>
        )}
        <button
          type="button"
          onClick={resetAvatarSplitContent}
          className="p-2 rounded-lg hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
          title="Reset panel"
          aria-label="Reset panel"
        >
          <RotateCcw className="w-4 h-4" />
        </button>
      </header>

      <div className="flex-1 relative min-h-0 bg-black">
        {showContent && frameUrl ? (
          <iframe
            key={`${pageId}-${reloadKey}`}
            src={frameUrl}
            title={item?.title ?? 'SELF Labs'}
            className="absolute inset-0 w-full h-full border-0"
            loading="eager"
          />
        ) : null}

        {!showContent && (
          <div className="avatar-split-panel-placeholder absolute inset-0 flex flex-col items-center justify-center p-6 text-center">
            <div className="avatar-split-panel-placeholder__inner">
              <p className="avatar-split-panel-placeholder__eyebrow">Content panel</p>
              <p className="avatar-split-panel-placeholder__title">
                This is where the Ambassador will show requested content
              </p>
              <p className="avatar-split-panel-placeholder__subtitle">
                Ask Milan about SELF Phone, products, partnerships, or demos — or use Explore
                topics below.
              </p>
            </div>
          </div>
        )}
      </div>
    </aside>
  );
}
